import { useFlags } from 'flagsmith/react';
import { get } from 'lodash';

import type { SocialProfileNetworkType } from '@/types/socialProfileType';

export type ClickhouseMediaAnalyticsType = {
    activeNetworks: SocialProfileNetworkType[];
    isActiveForNetwork: (network: SocialProfileNetworkType) => boolean;
};

/**
 * Hook to check if clickhouse media analytics feature is active for a given network
 * Reads the 'clickhouse_media_analytics_active' feature flag which should contain
 * an array of social profile keys (e.g., ['x', 'instagram'])
 */
export const useClickhouseMediaAnalytics = (): ClickhouseMediaAnalyticsType => {
    const flags = useFlags(['clickhouse_media_analytics_active']);
    const isFeatureFlagEnabled = get(flags, 'clickhouse_media_analytics_active.enabled', false);
    const flagValue = get(flags, 'clickhouse_media_analytics_active.value', '[]');

    const getFlagValue = () => {
        if (isFeatureFlagEnabled && typeof flagValue === 'string') {
            try {
                return JSON.parse(flagValue);
            } catch (error) {
                console.error(
                    'Failed to parse clickhouse_media_analytics_active flag value:',
                    error
                );
                return [];
            }
        }

        return [];
    };

    let activeNetworks: SocialProfileNetworkType[] = getFlagValue();

    /**
     * Check if the feature is active for a specific network
     * @param network - Social network type (e.g., 'x', 'instagram')
     * @returns true if the network is in the active list
     */
    const isActiveForNetwork = (network: SocialProfileNetworkType): boolean => {
        if (!Array.isArray(activeNetworks) || !isFeatureFlagEnabled) {
            return false;
        }

        return activeNetworks.includes(network);
    };

    return { activeNetworks, isActiveForNetwork };
};
